import classNames from "classnames";
import { omit, uniqueId } from "lodash";
import { Input } from "PFComponents/text/input";
import TextArea from "PFComponents/text/text_area";
import PropTypes from "prop-types";
import React from "react";

import { ActionIcon } from "../action_icon";
import { Icon } from "../icon";
import Tooltip from "../tooltip/tooltip";
import css from "./input_field_set.module.scss";

class InputFieldSet extends React.Component {
  constructor(props) {
    super(props);
    this.tagId = props.id || uniqueId(props.name);
    this.inputTagRef = props.inputTagRef || React.createRef();

    this.state = {
      showPassword: false,
      isFocused: false,
      valueLength: props.value ? String(props.value).length : 0
    };
  }

  static getDerivedStateFromProps(nextProps) {
    if (nextProps.controlledValue) {
      return {
        valueLength: nextProps.value ? String(nextProps.value).length : 0
      };
    }
    return {};
  }

  componentDidMount = () => {
    if (this.inputTagRef.current && this.props.autofocus) {
      this.inputTagRef.current.focus();
    }
  };

  handleChange = (value, event) => {
    this.setState({
      valueLength: String(value).length || 0
    });

    this.props.handleChange && this.props.handleChange(value, event);
  };

  handleFocus = (...args) => {
    const { handleFocus } = this.props;
    this.setState({ isFocused: true });
    handleFocus && handleFocus(...args);
  };

  handleBlur = (...args) => {
    const { handleBlur } = this.props;
    this.setState({ isFocused: false });
    handleBlur && handleBlur(...args);
  };

  handlePasswordToggle = () => {
    const { showPassword } = this.state;
    this.setState({ showPassword: !showPassword });
  };

  render() {
    const {
      kind,
      inputType,
      whiteStyle,
      qaId,
      rootClassName,
      style,
      innerStyle,
      locked,
      active,
      onMouseDown,
      handleKeyDown,
      handleKeyUp,
      placeholder,
      readOnly,
      disabled,
      displayValue,
      displayValues,
      bareInput,
      onAutoSize,
      noPadding,
      showPasswordToggle,
      inputStyle,
      error,
      warning,
      min,
      max,
      classes,
      inputProps,
      label,
      title,
      ...rest
    } = this.props;

    const { isFocused, valueLength, showPassword } = this.state;
    const placeholderPresent = !!placeholder;

    let calcInputType = inputType;

    if (inputType === "password" && showPassword) {
      calcInputType = "text";
    }

    const InputTag = inputType === "textarea" ? TextArea : Input;

    const className = classNames(
      {
        [css.labeledText]: label,
        [css.noPadding]: noPadding,
        [css.readOnly]: readOnly,
        [css.bareInput]: bareInput,
        [css.white]: whiteStyle,
        [css.focused]: isFocused,
        [css.active]:
          active || isFocused || !!valueLength || displayValue || displayValues || placeholderPresent,
        [css.password]: inputType === "password" && showPasswordToggle,
        [css.locked]: locked,
        [css.disabled]: disabled
      },
      rootClassName,
      classes.root
    );

    const hiddenStyle = {
      position: "absolute",
      zIndex: -1
    };

    const passwordShowPadding = showPasswordToggle && inputType === "password" ? { paddingRight: 50 } : null;
    const finalStyle = {
      ...inputStyle,
      ...rest.style,
      ...passwordShowPadding,
      ...(displayValues?.length && !isFocused ? hiddenStyle : {})
    };

    // TODO: [PROF-2909] remove when refactoring inputFieldSet
    const inputRestProps = omit(rest, [
      "autofocus",
      "tip",
      "lockedTip",
      "inputTagRef",
      "handleChange",
      "lockedTipPosition",
      "icon",
      "minVisibleMaxLengthFactor"
    ]);

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div className={className} style={style} data-qa-id={qaId} onMouseDown={onMouseDown}>
        <div
          className={classNames(
            css.wrap,
            {
              [css.inputError]: error,
              [css.inputWarning]: warning,
              [css.bordered]: kind === "bordered",
              [css.blank]: kind === "blank",
              [css.locked]: locked
            },
            classes.inputContainer
          )}
          style={innerStyle}
        >
          {(displayValue || displayValue === 0) && <div className={css.value}>{displayValue}</div>}
          {displayValues && <div className={css.values}>{displayValues}</div>}
          <InputTag
            {...inputRestProps}
            ref={this.inputTagRef}
            id={this.tagId}
            readOnly={readOnly}
            placeholder={placeholder}
            disabled={locked || disabled}
            type={calcInputType}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onAutoSize={onAutoSize}
            style={finalStyle}
            min={min}
            max={max}
            className={classes.input}
            title={title || label}
            {...inputProps}
          />
          {this.renderLabel()}
          {this.renderPasswordToggle()}
          {this.renderIcon()}
          {this.renderLockedIcon()}
        </div>
        {this.renderMaxLengthLabel()}
        {this.renderError()}
        {this.renderTip()}
      </div>
    );
  }

  renderLabel() {
    const { label, required, error } = this.props;
    const text = required ? `${label} *` : label;
    return (
      label && (
        <label htmlFor={this.tagId} className={classNames(css.label, { [css.labelError]: error })}>
          <span>{text}</span>
        </label>
      )
    );
  }

  renderIcon() {
    const { icon, locked, classes } = this.props;
    return !locked && icon && <div className={classNames(css.icon, classes.icon)}>{icon}</div>;
  }

  renderPasswordToggle() {
    const { inputType, showPasswordToggle } = this.props;
    const { showPassword } = this.state;
    return (
      showPasswordToggle &&
      inputType === "password" && (
        <ActionIcon
          name={showPassword ? "shown" : "hidden"}
          onClick={this.handlePasswordToggle}
          classes={{ root: css.passwordToggle }}
        />
      )
    );
  }

  renderMaxLengthLabel() {
    const { maxLength, minVisibleMaxLengthFactor, chatStyleMaxLength } = this.props;
    const { valueLength } = this.state;

    const className = classNames(css.maxLength, {
      [css.waggleAnimation]: valueLength === maxLength,
      [css.maxLengthChat]: chatStyleMaxLength
    });

    return (
      maxLength &&
      valueLength > minVisibleMaxLengthFactor * maxLength && (
        <div className={className}>
          {valueLength} <span>/</span> {maxLength}
        </div>
      )
    );
  }

  renderLockedIcon() {
    const { locked, lockedTip, lockedTipPosition } = this.props;
    const classes = classNames(css.icon, css.lockIcon);
    return (
      locked && (
        <Tooltip placement={lockedTipPosition} content={<span>{lockedTip}</span>}>
          <div className={classes}>
            <Icon name="locked" size="sm" />
          </div>
        </Tooltip>
      )
    );
  }

  renderError() {
    const { error, warning, classes } = this.props;

    const content = error || warning;

    return (
      content && (
        <div
          className={classNames({ [css.error]: error, [css.warning]: warning }, classes.error)}
          data-target-id="input_field_set__error"
        >
          {Array.isArray(content) ? (
            content.map((err) => <span key={err}>{err}</span>)
          ) : (
            <span>{content}</span>
          )}
        </div>
      )
    );
  }

  renderTip() {
    const { tip, error, warning } = this.props;

    return (
      !error &&
      !warning &&
      tip && (
        <div className={css.tip}>
          <span>{tip}</span>
        </div>
      )
    );
  }
}

InputFieldSet.propTypes = {
  kind: PropTypes.oneOf(["bordered", "blank"]),
  inputType: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  controlledValue: PropTypes.bool,
  autoComplete: PropTypes.bool,
  disabled: PropTypes.bool,
  noPadding: PropTypes.bool,
  inputStyle: PropTypes.object,

  handleChange: PropTypes.func,
  handleClick: PropTypes.func,
  handleKeyUp: PropTypes.func,
  handleKeyDown: PropTypes.func,
  handleFocus: PropTypes.func,
  handleBlur: PropTypes.func,

  label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  title: PropTypes.string,
  displayValue: PropTypes.node,
  displayValues: PropTypes.node,
  tip: PropTypes.string,
  qaId: PropTypes.string,
  icon: PropTypes.element,

  active: PropTypes.bool,
  readOnly: PropTypes.bool,
  bareInput: PropTypes.bool,
  required: PropTypes.bool,

  locked: PropTypes.bool,
  lockedTip: PropTypes.string,
  lockedTipPosition: PropTypes.oneOf(["top", "bottom"]),

  error: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  warning: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),

  rootClassName: PropTypes.string,
  style: PropTypes.object,
  innerStyle: PropTypes.object,

  maxLength: PropTypes.number,
  minVisibleMaxLengthFactor: PropTypes.number,
  showPasswordToggle: PropTypes.bool,
  whiteStyle: PropTypes.bool,
  onMouseDown: PropTypes.func,
  onAutoSize: PropTypes.func,

  min: PropTypes.number,
  max: PropTypes.number,

  inputTagRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.instanceOf(Input)])
  }),
  chatStyleMaxLength: PropTypes.bool,
  autofocus: PropTypes.bool,
  classes: PropTypes.objectOf(PropTypes.string),
  inputProps: PropTypes.object
};

InputFieldSet.defaultProps = {
  tip: "",
  qaId: null,
  displayValue: null,
  displayValues: null,

  active: false,
  readOnly: false,
  bareInput: false,

  locked: false,
  lockedTip: "",
  lockedTipPosition: "top",

  error: null,
  warning: null,

  maxLength: null,
  minVisibleMaxLengthFactor: -1,
  showPasswordToggle: false,
  whiteStyle: false,
  noPadding: false,
  autofocus: false,
  classes: {},
  inputProps: {}
};

export default InputFieldSet;
